'use client';

import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { supabaseClient } from '../lib/supabase';
import { Users } from '../lib/types';
import { User, Session, AuthChangeEvent } from '@supabase/supabase-js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

interface AuthState {
  user: User | null;
  profile: Users | null;
  loading: boolean;
  signOut: () => Promise<void>;
  signUp: (email: string, password: string, name?: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithProvider: (provider: 'google' | 'twitter') => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateProfile: (updates: Partial<Users>) => Promise<void>;
}

export const AuthContext = createContext<AuthState | undefined>(undefined);

// Storage keys
const STORAGE_KEYS = {
  USER: 'axist_auth_user',
  PROFILE: 'axist_auth_profile',
};

// SSR-safe storage helpers
const getStored = (key: string) => {
  if (typeof window === 'undefined') return null;
  const val = localStorage.getItem(key);
  return val ? JSON.parse(val) : null;
};

const setStored = (key: string, val: any) => {
  if (typeof window === 'undefined') return;
  if (val) localStorage.setItem(key, JSON.stringify(val));
  else localStorage.removeItem(key);
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Users | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Initialize from storage on mount to avoid hydration mismatch
  useEffect(() => {
    const storedUser = getStored(STORAGE_KEYS.USER);
    const storedProfile = getStored(STORAGE_KEYS.PROFILE);
    if (storedUser) setUser(storedUser);
    if (storedProfile) setProfile(storedProfile);
    if (storedUser) setLoading(false);
  }, []);

  // TanStack Query for the profile
  const { data: profileData, refetch: refetchProfile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user) return null;
      console.log('🔄 [Auth] Refreshing profile from server...');
      const { data, error } = await supabaseClient
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      return data as Users;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Sync state with profile data from query
  useEffect(() => {
    if (profileData) {
      setProfile(profileData);
      setStored(STORAGE_KEYS.PROFILE, profileData);
    }
  }, [profileData]);

  // Auth initialization and listener
  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        // Parallel check for session while UI shows cached data
        const { data: { session }, error } = await supabaseClient.auth.getSession();
        if (error) throw error;

        const currentUser = session?.user ?? null;

        if (mounted) {
          setUser(currentUser);
          setStored(STORAGE_KEYS.USER, currentUser);

          if (!currentUser) {
            setProfile(null);
            setStored(STORAGE_KEYS.PROFILE, null);
            queryClient.setQueryData(['profile', null], null);
          }
        }
      } catch (err) {
        console.error('[Auth] Init error:', err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabaseClient.auth.onAuthStateChange(
      async (event: AuthChangeEvent, session: Session | null) => {
        if (!mounted) return;

        console.log('[Auth] Event:', event);
        const currentUser = session?.user ?? null;

        if (event === 'SIGNED_OUT') {
          setUser(null);
          setProfile(null);
          setStored(STORAGE_KEYS.USER, null);
          setStored(STORAGE_KEYS.PROFILE, null);
          queryClient.clear();
        } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
          setUser(currentUser);
          setStored(STORAGE_KEYS.USER, currentUser);
          // Query will automatically refetch when user.id changes due to enabled: !!user
        }

        setLoading(false);
      }
    );

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, [queryClient]);

  const signUp = async (email: string, password: string, name?: string) => {
    const { error } = await supabaseClient.auth.signUp({
      email,
      password,
      options: {
        data: name ? { name } : undefined,
        emailRedirectTo: `${window.location.origin}/auth/verify`,
      },
    });
    if (error) throw error;
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabaseClient.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signInWithProvider = async (provider: 'google' | 'twitter') => {
    const { error } = await supabaseClient.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabaseClient.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw error;
  };

  const updateProfile = async (updates: Partial<Users>) => {
    if (!user) throw new Error('No user logged in');
    const { error } = await (supabaseClient.from('users') as any)
      .update(updates)
      .eq('id', user.id);
    if (error) throw error;

    if (profile) {
      const nextProfile = { ...profile, ...updates } as Users;
      setProfile(nextProfile);
      setStored(STORAGE_KEYS.PROFILE, nextProfile);
      queryClient.setQueryData(['profile', user.id], nextProfile);
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      // Immediate local clear
      setUser(null);
      setProfile(null);
      setStored(STORAGE_KEYS.USER, null);
      setStored(STORAGE_KEYS.PROFILE, null);
      queryClient.clear();

      console.log('[Auth] Initiating server-side sign out...');
      await Promise.race([
        supabaseClient.auth.signOut(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('TIMEOUT')), 2000))
      ]).catch(err => {
        console.warn('[Auth] Sign out call had an issue or timed out:', err);
      });

      console.log('[Auth] Sign out process complete');
    } catch (error) {
      console.error('Logout failed:', error);
      // Ensure local state is cleared even if call fails
      setUser(null);
      setProfile(null);
      setStored(STORAGE_KEYS.USER, null);
      setStored(STORAGE_KEYS.PROFILE, null);
      queryClient.clear();
    }
  };

  const value = {
    user,
    profile,
    loading,
    signOut,
    signUp,
    signIn,
    signInWithProvider,
    resetPassword,
    updateProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthState => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
